import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  confirmPhoneVerification,
  getPhoneVerificationDelivery,
  sendPhoneVerification,
  updateVerificationPhone,
} from '../../../api/phone_verification';
import { handleUserRedirect } from '../../../utils/userRedirect';

import Context from '../../../utils/context';

import styles from '../sign_in/index.module.css';

const CODE_LENGTH = 6;
const EMPTY_DIGITS = Array(CODE_LENGTH).fill('');
const CARRIER_FAILURE = "This number can't receive texts. Enter a mobile number.";

export default function VerifyPhone() {
  const context = useContext(Context);
  const history = useHistory();
  const [digits, setDigits] = useState(EMPTY_DIGITS);
  const [phoneHint, setPhoneHint] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [replacementPhone, setReplacementPhone] = useState('');
  const [showPhoneChange, setShowPhoneChange] = useState(false);
  const digitRefs = useRef([]);
  const sent = useRef(false);

  const sendCode = () => {
    setLoading(true);
    setError('');
    sendPhoneVerification(
      (data) => {
        setLoading(false);
        setPhoneHint(data.phone_hint || null);
      },
      (message) => {
        setLoading(false);
        setError(message);
        setShowPhoneChange(true);
      }
    );
  };

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    sendCode();
  }, []);

  useEffect(() => {
    let stopped = false;
    const checkDelivery = () => {
      getPhoneVerificationDelivery((data) => {
        if (stopped || data.status !== 'failed') return;
        setError(CARRIER_FAILURE);
        setShowPhoneChange(true);
      });
    };

    checkDelivery();
    const timer = window.setInterval(checkDelivery, 3000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, []);

  const finish = (user) => {
    context.auth.currentUser = user;
    // Preserve any validated return_to passed through to this page
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('return_to') || '';
    handleUserRedirect(user, history, { isImpersonation: false, returnTo });
  };

  const submitCode = (rawCode) => {
    if (rawCode.length !== CODE_LENGTH) return;
    setLoading(true);
    setError('');
    confirmPhoneVerification(
      rawCode,
      (data) => {
        setLoading(false);
        finish(data.user);
      },
      (message) => {
        setLoading(false);
        setError(message);
        setDigits(EMPTY_DIGITS);
        if (digitRefs.current[0]) digitRefs.current[0].focus();
      }
    );
  };

  const changePhone = () => {
    const phoneDigits = replacementPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);
    setError('');
    updateVerificationPhone(
      replacementPhone,
      (data) => {
        setLoading(false);
        setPhoneHint(data.phone_hint || null);
        setReplacementPhone('');
        setShowPhoneChange(false);
        setDigits(EMPTY_DIGITS);
      },
      (message) => {
        setLoading(false);
        setError(message);
      }
    );
  };

  const fillDigits = (characters) => {
    const next = [...EMPTY_DIGITS];
    for (let i = 0; i < characters.length && i < CODE_LENGTH; i += 1) {
      next[i] = characters[i];
    }
    setDigits(next);

    const focusIndex = Math.min(characters.length, CODE_LENGTH - 1);
    if (digitRefs.current[focusIndex]) digitRefs.current[focusIndex].focus();

    if (characters.length >= CODE_LENGTH) submitCode(next.join(''));
  };

  const handleDigitChange = (index, rawValue) => {
    if (error) setError('');
    const sanitized = rawValue.replace(/\D/g, '');

    // SMS autofill (and some paste paths) drop the entire code into a single
    // field, so spread it across the inputs instead of keeping only one digit.
    if (sanitized.length > 1) {
      fillDigits(sanitized);
      return;
    }

    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);
    if (sanitized && index < CODE_LENGTH - 1 && digitRefs.current[index + 1]) {
      digitRefs.current[index + 1].focus();
    }
    const joined = next.join('');
    if (joined.length === CODE_LENGTH && next.every((digit) => digit !== '')) {
      submitCode(joined);
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      const prevRef = digitRefs.current[index - 1];
      if (prevRef) prevRef.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      digitRefs.current[index - 1].focus();
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      digitRefs.current[index + 1].focus();
    }
  };

  const handleDigitPaste = (event) => {
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    if (error) setError('');
    fillDigits(pasted);
  };

  const sentTo = phoneHint ? `a phone number ending in ${phoneHint}` : 'your phone';

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <h1 className={styles.auth_heading}>Confirm your phone</h1>
          <p className={styles.auth_subtitle}>
            We sent a verification code to {sentTo}. You can use PatientPartner after you enter it.
          </p>
          <div className={styles.check_email_digits} onPaste={handleDigitPaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(node) => {
                  digitRefs.current[index] = node;
                }}
                type='text'
                inputMode='numeric'
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                className={styles.check_email_digit}
                value={digit}
                onChange={(event) => handleDigitChange(index, event.target.value)}
                onKeyDown={(event) => handleDigitKeyDown(index, event)}
                onFocus={(event) => event.target.select()}
                maxLength={1}
                disabled={loading}
              />
            ))}
          </div>
          {error && <p className={styles.auth_error}>{error}</p>}
          <button
            type='button'
            className={`${styles.auth_link} ${styles.auth_link_center}`}
            onClick={sendCode}
            disabled={loading}
          >
            {loading ? 'Sending…' : 'Resend code'}
          </button>
          <button
            type='button'
            className={`${styles.auth_link} ${styles.auth_link_center}`}
            onClick={() => setShowPhoneChange((open) => !open)}
            disabled={loading}
          >
            Wrong number?
          </button>
          {showPhoneChange && (
            <div className={styles.auth_field}>
              <label className={styles.auth_field_label} htmlFor='verify-replacement-phone'>
                Mobile number
              </label>
              <input
                id='verify-replacement-phone'
                type='tel'
                className={styles.auth_input}
                value={replacementPhone}
                onChange={(event) => setReplacementPhone(event.target.value)}
                placeholder='(555) 555-5555'
              />
              <button type='button' className={styles.auth_button} onClick={changePhone} disabled={loading}>
                {loading ? 'Sending…' : 'Text this number'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
